import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Note: We don't set axios.defaults.withCredentials globally anymore
// This was causing issues on Safari/iOS. Instead, we rely on JWT tokens in localStorage.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fanbound_token'));
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || 
        window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    
    // Check if we have a token in localStorage
    const storedToken = localStorage.getItem('fanbound_token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      setToken(storedToken);
    }
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // No valid session - clear any stale token
      localStorage.removeItem('fanbound_token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      // Check for session cookie (Google OAuth)
      checkSession();
    }
  }, [token, checkSession]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      // Use fetch instead of axios to avoid any global axios interceptors/defaults
      // This fixes Safari/iOS compatibility issues with CORS + credentials
      const response = await fetch(`${API_URL}/auth/google/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId }),
        // Explicitly disable credentials - Safari blocks CORS with credentials
        credentials: 'omit'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Authentication failed');
      }
      
      const data = await response.json();
      
      // Response includes both user and token
      const { user: userData, token: jwtToken } = data;
      
      // Store JWT token in localStorage (works on all browsers including Safari)
      if (jwtToken) {
        localStorage.setItem('fanbound_token', jwtToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
        setToken(jwtToken);
      }
      
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Google auth failed:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { user: userData, token: newToken } = response.data;
    localStorage.setItem('fanbound_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, displayName, role = 'fan') => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      display_name: displayName,
      role
    });
    const { user: userData, token: newToken } = response.data;
    localStorage.setItem('fanbound_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const registerInfluencer = async (data) => {
    const response = await axios.post(`${API_URL}/auth/influencer-signup`, {
      email: data.email,
      password: data.password,
      display_name: data.display_name,
      username: data.username,
      bio: data.bio,
      social_handles: data.social_handles,
      follower_count: data.follower_count
    });
    const { user: userData, token: newToken } = response.data;
    localStorage.setItem('fanbound_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('fanbound_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const currentToken = token || localStorage.getItem('fanbound_token');
    if (currentToken) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        if (!token && currentToken) {
          setToken(currentToken);
        }
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  // Function to update token (used after upgrade)
  const updateToken = (newToken) => {
    localStorage.setItem('fanbound_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    registerInfluencer,
    handleGoogleCallback,
    logout,
    refreshUser,
    updateToken,
    isAuthenticated: !!user,
    isInfluencer: user?.role === 'influencer',
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
